import * as React from 'react';
import {View, StyleSheet} from 'react-native';
import {Edge, SafeAreaView} from 'react-native-safe-area-context';
import {NavigationHelpersContext, TabNavigationState, useTheme} from '@react-navigation/native';
import {ScreenContainer} from 'react-native-screens';

import ResourceSavingScene from './ResourceSavingScene';
import {TabBarCustom} from '../index';
import {LocalParamList} from './types';
import type {
  BottomTabNavigationConfig,
  BottomTabDescriptorMap,
  BottomTabNavigationHelpers,
  BottomTabBarProps,
} from './types';
import {tabBarTopOffset} from '../variables';
import {Screens} from '../../../routing';

const routesWithFullscreenBackground: string[] = [Screens.CardReissue];
const horizontalSafeAreaEdges: Edge[] = ['left', 'right'];

type Props = BottomTabNavigationConfig & {
  state: TabNavigationState<LocalParamList>;
  navigation: BottomTabNavigationHelpers;
  descriptors: BottomTabDescriptorMap;
};

type State = {
  loaded: number[];
};

function SceneContent({isFocused, children}: {isFocused: boolean; children: React.ReactNode}) {
  const {colors} = useTheme();

  return (
    <View
      accessibilityElementsHidden={!isFocused}
      importantForAccessibility={isFocused ? 'auto' : 'no-hide-descendants'}
      style={[styles.content, {backgroundColor: colors.background}]}>
      {children}
    </View>
  );
}

export default class BottomTabView extends React.Component<Props, State> {
  static defaultProps = {
    lazy: true,
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const {index} = nextProps.state;

    return {
      // Set the current tab to be loaded if it was not loaded before
      loaded: prevState.loaded.includes(index) ? prevState.loaded : [...prevState.loaded, index],
    };
  }

  state = {
    loaded: [this.props.state.index],
  };

  private renderTabBar = () => {
    const {
      tabBar = (props: BottomTabBarProps) => <TabBarCustom {...(props as any)} />,
      tabBarOptions,
      state,
      navigation,
      descriptors,
    } = this.props;
    return tabBar({
      ...tabBarOptions,
      state: state,
      descriptors: descriptors,
      navigation: navigation,
    } as BottomTabBarProps);
  };

  render() {
    const {state, descriptors, navigation, lazy} = this.props;
    const {routes} = state;
    const {loaded} = this.state;
    const focusedRouteName = routes[state.index]?.name;
    const safeAreaEdges = routesWithFullscreenBackground.includes(focusedRouteName)
      ? horizontalSafeAreaEdges
      : undefined;

    return (
      <NavigationHelpersContext.Provider value={navigation}>
        <SafeAreaView style={styles.container} edges={safeAreaEdges}>
          <ScreenContainer style={[styles.pages, {marginBottom: -tabBarTopOffset}]}>
            {routes.map((route, index) => {
              const descriptor = descriptors[route.key];
              const {unmountOnBlur} = descriptor.options;
              const isFocused = state.index === index;

              if (unmountOnBlur && !isFocused) {
                return null;
              }

              if (lazy && !loaded.includes(index) && !isFocused) {
                // Don't render a screen if we've never navigated to it
                return null;
              }

              return (
                <ResourceSavingScene key={route.key} style={StyleSheet.absoluteFill} isVisible={isFocused}>
                  <SceneContent isFocused={isFocused}>{descriptor.render()}</SceneContent>
                </ResourceSavingScene>
              );
            })}
          </ScreenContainer>
          {this.renderTabBar()}
        </SafeAreaView>
      </NavigationHelpersContext.Provider>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  pages: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
});
